import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Textarea,
  makeStyles,
  shorthands,
  tokens,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  QuestionCircle20Regular,
  MoreHorizontal20Regular,
  Person20Regular,
  Edit20Regular,
  Link20Regular,
  Settings20Regular,
  Send20Regular,
  Eye20Regular,
  EyeOff20Regular,
} from '@fluentui/react-icons';
import { toSentenceCase } from '../utils/utils';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemName: string;
  itemType: string;
  shareUrl: string;
  onCopyLink?: () => void;
  onSend?: (recipients: string, message: string, canEdit: boolean) => void;
}

const useStyles = makeStyles({
  dialogSurface: {
    width: '100%',
    maxWidth: '480px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  titleText: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '16px',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  inputWrapper: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  inputIcon: {
    paddingLeft: '12px',
    color: tokens.colorNeutralForeground3,
  },
  input: {
    flex: 1,
  },
  permissionButton: {
    marginRight: '4px',
  },
  messageWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  messageIcon: {
    paddingLeft: '12px',
    paddingTop: '10px',
    color: tokens.colorNeutralForeground3,
  },
  textarea: {
    flex: 1,
    minHeight: '80px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  copyLinkButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
});

export const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  itemName,
  itemType,
  shareUrl,
  onCopyLink,
  onSend,
}) => {
  const styles = useStyles();
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [canEdit, setCanEdit] = useState(true);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    onCopyLink?.();
  };

  const handleSend = () => {
    const subject = `${itemType}: ${itemName} has been shared with you`;
    const body = message
      ? `${message}\n\n${shareUrl}`
      : shareUrl;

    // TO DO use an email service instead of mailto
    // Open mailto link
    const mailtoUrl = `mailto:${encodeURIComponent(recipients)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');

    onSend?.(recipients, message, canEdit);
    // Reset form
    setRecipients('');
    setMessage('');
    onClose();
  };

  const handleClose = () => {
    setRecipients('');
    setMessage('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <div className={styles.titleActions}>
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={handleClose}
                  size="small"
                />
              </div>
            }
          >
            Share "{toSentenceCase(itemType)} {itemName}"
          </DialogTitle>
          <DialogContent>
            <div className={styles.content}>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}>
                  <Person20Regular />
                </span>
                <Input
                  className={styles.input}
                  placeholder="Enter a name or email"
                  value={recipients}
                  onChange={(_, data) => setRecipients(data.value)}
                  appearance="underline"
                  style={{ border: 'none', outline: 'none' }}
                />
              </div>

              <div className={styles.messageWrapper}>
                <span className={styles.messageIcon}>
                  <Edit20Regular />
                </span>
                <Textarea
                  className={styles.textarea}
                  placeholder="Add a message"
                  value={message}
                  onChange={(_, data) => setMessage(data.value)}
                  appearance="filled-lighter"
                  style={{ border: 'none' }}
                  resize="none"
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button
              appearance="outline"
              icon={<Link20Regular />}
              onClick={handleCopyLink}
            >
              Copy link
            </Button>
            <Button
              appearance="primary"
              icon={<Send20Regular />}
              onClick={handleSend}
              disabled={!recipients.trim()}
            >
              Send
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ShareDialog;
